import { useId } from 'react';
import { WorkOrderStatus, WorkOrderType, type WorkOrderDto } from '@signalvote/shared';
import { Download, Upload } from 'lucide-react';
import { PendingButton, buttonVariants } from '@/components/ui';
import { formatInCampaignTimeZone } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';

interface TextSendResultsReportProps {
  workOrder: WorkOrderDto;
  /** Signs a fresh link to the report and follows it. */
  onDownload: () => void;
  downloading?: boolean;
  downloadError?: string | null;
  /** Attaches or replaces the PDF. Only a platform admin's view passes this. */
  onAttach?: (file: File) => void;
  attaching?: boolean;
  attachError?: string | null;
}

/**
 * A Complete Text send's results report on its order page: a download once the PDF is attached, a
 * "on its way" note before, and for a platform admin the upload that attaches or replaces it.
 */
export function TextSendResultsReport({
  workOrder,
  onDownload,
  downloading = false,
  downloadError,
  onAttach,
  attaching = false,
  attachError,
}: TextSendResultsReportProps) {
  const inputId = useId();
  if (workOrder.type !== WorkOrderType.TextSend || workOrder.status !== WorkOrderStatus.Complete) return null;
  const { hasResultsReport, resultsReportUploadedAt } = workOrder.details;

  return (
    <section aria-label="Results report" className="space-y-2 rounded-md border p-3 text-sm">
      {hasResultsReport ? (
        <>
          <PendingButton className="w-full" pending={downloading} onClick={onDownload}>
            <Download className="size-4" aria-hidden />
            Download results report
          </PendingButton>
          {resultsReportUploadedAt && (
            <p className="text-xs text-muted-foreground">
              Added {formatInCampaignTimeZone(resultsReportUploadedAt)}.
            </p>
          )}
        </>
      ) : (
        <p className="text-muted-foreground">Your results report is on its way. We email it when it arrives.</p>
      )}
      {downloadError && (
        <p role="alert" className="text-sm text-destructive">
          {downloadError}
        </p>
      )}

      {onAttach && (
        <div className="space-y-1 border-t pt-2">
          <label
            htmlFor={inputId}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'cursor-pointer', attaching && 'pointer-events-none opacity-60')}
          >
            <Upload className="size-4" aria-hidden />
            {attaching ? 'Uploading…' : hasResultsReport ? 'Replace results report' : 'Attach results report'}
          </label>
          <input
            id={inputId}
            type="file"
            accept="application/pdf"
            className="sr-only"
            disabled={attaching}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onAttach(file);
            }}
          />
          <p className="text-xs text-muted-foreground">
            {hasResultsReport
              ? 'Replacing swaps the file. The campaign isn’t emailed again.'
              : 'The first report emails the campaign’s admins with the PDF attached.'}
          </p>
          {attachError && (
            <p role="alert" className="text-sm text-destructive">
              {attachError}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
